import { useState, useEffect, useCallback } from 'react'
import { ArrowPathIcon, QueueListIcon } from '@heroicons/react/24/outline'
import { PackagingJob, JobStatus as JobStatusType, isTerminalStatus, JOB_STATUS_LABELS } from '../types/job'
import { listJobs, getBulkJobStatus, cancelJob, stopJob, retryJob, deleteJob, getJob } from '../services/api'
import JobCard from '../components/JobCard'
import JobDetails from '../components/JobDetails'
import QueueMetrics from '../components/QueueMetrics'
import { PageHeader, EmptyState, Spinner } from '../components/ui'
import { errorText } from '../utils/errorText'

const POLL_INTERVAL = 5000 // 5 seconds per SC-002

type StatusFilter = JobStatusType | 'all'

/**
 * The job queue, laid out as the mock's Background Jobs page: header, Queue Health, the status
 * filter as a row of pills, then one row per job.
 *
 * The filter is pills rather than a dropdown because the mock puts every state on screen at once
 * — which state you are looking at is then readable without opening anything.
 */
export default function JobStatus() {
  const [jobs, setJobs] = useState<PackagingJob[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState<PackagingJob | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [page, setPage] = useState(0)
  const [metricsKey, setMetricsKey] = useState(0)
  const pageSize = 12

  // Initial load and filtered load
  const loadJobs = useCallback(async () => {
    try {
      const statusParam = statusFilter === 'all' ? undefined : [statusFilter]
      const response = await listJobs({
        status: statusParam,
        limit: pageSize,
        offset: page * pageSize,
      })
      setJobs(response.jobs as unknown as PackagingJob[])
      setTotal(response.total)
      setError(null)
    } catch (err: any) {
      setError(errorText(err, 'Failed to load jobs'))
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  // Poll for active job status updates
  const pollActiveJobs = useCallback(async () => {
    const activeJobIds = jobs
      .filter((j) => !isTerminalStatus(j.status))
      .map((j) => j.job_id)

    if (activeJobIds.length === 0) return

    try {
      const statuses = await getBulkJobStatus(activeJobIds)

      setJobs((prevJobs) =>
        prevJobs.map((job) => {
          const update = statuses[job.job_id]
          if (update) {
            return {
              ...job,
              status: update.status as JobStatusType,
              progress_pct: update.progress_pct,
              error_message: update.error_message,
            }
          }
          return job
        })
      )

      // Update selected job if it's being polled
      if (selectedJob && statuses[selectedJob.job_id]) {
        const update = statuses[selectedJob.job_id]
        setSelectedJob((prev) =>
          prev
            ? {
                ...prev,
                status: update.status as JobStatusType,
                progress_pct: update.progress_pct,
                error_message: update.error_message,
              }
            : null
        )
      }
    } catch (err) {
      console.error('Failed to poll job statuses:', err)
    }
  }, [jobs, selectedJob])

  // Initial load
  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  // Polling for active jobs
  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => !isTerminalStatus(j.status))
    if (!hasActiveJobs) return

    const intervalId = setInterval(pollActiveJobs, POLL_INTERVAL)
    return () => clearInterval(intervalId)
  }, [jobs, pollActiveJobs])

  /** Refresh reloads the list AND the queue counts — they describe the same queue, and a stale
   *  header over a fresh list reads as a contradiction. */
  const handleRefresh = () => {
    loadJobs()
    setMetricsKey((k) => k + 1)
  }

  const handleSelectJob = (job: PackagingJob) => {
    setSelectedJob(job)
    setDetailsOpen(true)
  }

  /** A recovery job and the failed job it retries link to each other in the details modal. */
  const handleOpenJob = async (jobId: string) => {
    try {
      setSelectedJob(jobs.find((j) => j.job_id === jobId) ?? (await getJob(jobId)))
    } catch (err: any) {
      setError(errorText(err, 'Failed to open job'))
    }
  }

  const handleCloseDetails = () => {
    setDetailsOpen(false)
    setSelectedJob(null)
  }

  const handleJobUpdated = () => {
    loadJobs()
    setDetailsOpen(false)
    setSelectedJob(null)
  }

  const handleCancel = async (jobId: string) => {
    try {
      await cancelJob(jobId)
      loadJobs()
    } catch (err: any) {
      setError(errorText(err, 'Failed to cancel job'))
    }
  }

  const handleRetry = async (jobId: string) => {
    try {
      await retryJob(jobId)
      loadJobs()
    } catch (err: any) {
      setError(errorText(err, 'Failed to retry job'))
    }
  }

  const handleStop = async (jobId: string) => {
    try {
      await stopJob(jobId)
      loadJobs()
    } catch (err: any) {
      setError(errorText(err, 'Failed to stop job'))
    }
  }

  const handleDelete = async (jobId: string) => {
    try {
      await deleteJob(jobId)
      loadJobs()
    } catch (err: any) {
      setError(errorText(err, 'Failed to delete job'))
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All Jobs' },
    { value: 'pending', label: 'Pending' },
    { value: 'queued', label: 'Queued' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ]

  return (
    <div className="p-8">
      <PageHeader
        title="Background Jobs"
        subtitle="Each packaging job builds one package in your catalog"
        actions={
          <button onClick={handleRefresh} className="btn-ghost">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <QueueMetrics refreshInterval={30000} reloadKey={metricsKey} />

      {error && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="btn-ghost btn-sm">
            Dismiss
          </button>
        </div>
      )}

      {/* Status filter. Every state is a pill, so the one in force is readable at a glance. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-fmuted">Filter by status:</span>
        {statusOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              setStatusFilter(opt.value)
              setPage(0)
            }}
            aria-pressed={statusFilter === opt.value}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              statusFilter === opt.value
                ? 'bg-accent text-white'
                : 'bg-panel3 text-fmuted hover:text-ftext'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-fmuted">
          <Spinner className="h-6 w-6" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<QueueListIcon className="h-7 w-7" />}
            title="No jobs in this state"
            hint={
              statusFilter === 'all'
                ? 'Submit a packaging job from the Catalog or Discovery.'
                : `No ${JOB_STATUS_LABELS[statusFilter as JobStatusType].toLowerCase()} jobs found.`
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <JobCard
              key={job.job_id}
              job={job}
              onSelect={handleSelectJob}
              onCancel={handleCancel}
              onStop={handleStop}
              onRetry={handleRetry}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-fmuted">
          <span>
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <JobDetails
        job={selectedJob}
        isOpen={detailsOpen}
        onClose={handleCloseDetails}
        onJobUpdated={handleJobUpdated}
        onOpenJob={handleOpenJob}
      />
    </div>
  )
}
